/**
 * Top-level shell that wraps every route.
 *
 * Responsibilities:
 *   - Bootstrap kiosk hardware config once on mount.
 *   - Gate everything behind staff auth, except `/login`.
 *   - Render the global floating Home button.
 *
 * NOTE: the inactivity timer was intentionally removed — the floating
 * "Beranda" button is the single, manual way home.
 */

import { type ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { HomeButton } from '../components/HomeButton';
import { getKioskConfig } from '../lib/api';
import { refreshKioskToken } from '../lib/api/identity';
import { useAuthStore } from '../store/authStore';
import { useUIStore } from '../store/uiStore';

import { ROUTES } from './routes';

interface Props {
  children: ReactNode;
}

export function KioskShell({ children }: Props): JSX.Element {
  const setKioskConfig = useUIStore((s) => s.setKioskConfig);
  const token = useAuthStore((s) => s.token);
  const kioskId = useAuthStore((s) => s.kioskId);
  const location = useLocation();

  useEffect(() => {
    if (!kioskId) return;
    getKioskConfig(kioskId)
      .then(setKioskConfig)
      .catch(() => {
        /* non-fatal: payment methods fall back to all enabled */
      });
  }, [kioskId, setKioskConfig]);

  // Auto-refresh JWT every 45 minutes to prevent session expiry (issue #40).
  // Retries up to 3× with linear back-off; forces re-login on total failure.
  useEffect(() => {
    if (!token) return;

    const intervalId = setInterval(async () => {
      const storedToken = localStorage.getItem('kiosk_staff_token');
      if (!storedToken) return;

      let newToken: string | null = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          newToken = await refreshKioskToken(storedToken);
          break;
        } catch {
          if (attempt < 3) {
            await new Promise<void>((r) => setTimeout(r, attempt * 5_000));
          }
        }
      }

      if (newToken) {
        const name = localStorage.getItem('kiosk_staff_name') ?? '';
        const role = localStorage.getItem('kiosk_staff_role') ?? '';
        const id = localStorage.getItem('kiosk_staff_id') ?? '';
        useAuthStore.getState().setAuth(newToken, name, role, id);
      } else {
        useAuthStore.getState().clearAuth();
      }
    }, 45 * 60 * 1_000);

    return () => clearInterval(intervalId);
  }, [token]);

  if (!token && location.pathname !== ROUTES.login) {
    return <Navigate to={ROUTES.login} replace />;
  }

  return (
    <>
      {children}
      <HomeButton />
    </>
  );
}
